import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Button, Card, Checkbox, DatePicker, Form, Radio, Select, Spin, Table, Tag, message } from 'antd'
import { faviconDynamicFunction } from '../../util/methods'
import axios from 'util/Api'
import moment from 'moment'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const { Option } = Select
const { RangePicker } = DatePicker

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

const formatDateTime = (value) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-')

const deviceLabel = (device) => {
    if (!device) return '-'
    const name = device.altName ? ` (${device.altName})` : ''
    return `${device.assetCode || '-'}${name}`
}

const soldDateColumn = {
    title: 'Sold Date',
    key: 'soldDate',
    render: (value, record) => (record.soldDate ? formatDateTime(record.soldDate) : '-')
}

// All report types, in display order.
const REPORTS = [
    { key: 'gps', label: 'GPS attached to asset', endpoint: '/billing/attached-gps' },
    { key: 'fuelSensor', label: 'Fuel sensor via GPS', endpoint: '/billing/attached-fuel-sensors' },
    { key: 'ble', label: 'Standalone BLE', endpoint: '/billing/attached-ble' },
    { key: 'manualFuel', label: 'Manual Fuel Assets', endpoint: '/billing/manual-fuel-assets' },
    { key: 'meterOn', label: 'Assets with meter', endpoint: '/billing/assets-with-meter' },
    { key: 'meterOff', label: 'Assets without meter', endpoint: '/billing/assets-without-meter' },
    { key: 'assetCount', label: 'Asset count', endpoint: '/billing/asset-count' }
]

const itemsForReport = (orgGroup, reportType) => {
    if (reportType === 'fuelSensor') return (orgGroup && orgGroup.sensors) || []
    if (reportType === 'ble') return (orgGroup && orgGroup.bleDevices) || []
    if (reportType === 'manualFuel' || reportType === 'meterOn' || reportType === 'meterOff' || reportType === 'assetCount') {
        return (orgGroup && orgGroup.assets) || []
    }
    return (orgGroup && orgGroup.gpsDevices) || []
}

// Maps the per-organisation billing response into parent rows (org + count) whose detail rows are
// the individual records.
const buildGroups = (data, reportType) =>
    (data || []).map((orgGroup, groupIndex) => {
        const organisation = (orgGroup && orgGroup.organisation) || {}
        const items = itemsForReport(orgGroup, reportType)
        return {
            key: String(organisation._id || `org-${groupIndex}`),
            organisationName: organisation.name || '-',
            count: typeof orgGroup.count === 'number' ? orgGroup.count : items.length,
            countableCount: typeof orgGroup.countableCount === 'number' ? orgGroup.countableCount : 0,
            items: items.map((item, itemIndex) => ({
                key: `${organisation._id || groupIndex}-${itemIndex}`,
                ...item
            }))
        }
    })

// --- column definitions --------------------------------------------------

const detailTailColumns = [
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (asset) => deviceLabel(asset) },
    { title: 'Attached Date', dataIndex: 'attachedDate', key: 'attachedDate', render: (value) => formatDateTime(value) },
    {
        title: 'Deattached Date',
        dataIndex: 'deattachedDate',
        key: 'deattachedDate',
        render: (value, record) =>
            record.stillAttached ? <Tag color="green">Still attached</Tag> : formatDateTime(value)
    },
    soldDateColumn
]

const gpsDetailColumns = [
    { title: 'GPS Device', dataIndex: 'gpsDevice', key: 'gpsDevice', render: (gps) => deviceLabel(gps) },
    { title: 'GPRS/IMEI Id', key: 'tagDeviceId', render: (value, record) => (record.gpsDevice && record.gpsDevice.tagDeviceId) || '-' },
    ...detailTailColumns
]

const fuelSensorDetailColumns = [
    { title: 'Fuel Sensor', dataIndex: 'fuelSensor', key: 'fuelSensor', render: (sensor) => deviceLabel(sensor) },
    { title: 'Fuel Sensor Id', key: 'fuelSensorId', render: (value, record) => (record.fuelSensor && record.fuelSensor.fuelSensorId) || '-' },
    { title: 'GPS Device', dataIndex: 'gpsDevice', key: 'gpsDevice', render: (gps) => deviceLabel(gps) },
    ...detailTailColumns
]

const bleDetailColumns = [
    { title: 'BLE Device', dataIndex: 'bleDevice', key: 'bleDevice', render: (ble) => deviceLabel(ble) },
    { title: 'BLE Tag Id', key: 'bleTagId', render: (value, record) => (record.bleDevice && record.bleDevice.bleTagId) || '-' },
    ...detailTailColumns
]

const manualFuelDetailColumns = [
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (asset) => deviceLabel(asset) },
    { title: 'Status', key: 'status', render: (value, record) => (record.asset && record.asset.status) || '-' },
    { title: 'Fuel Entries', dataIndex: 'fuelEntryCount', key: 'fuelEntryCount', align: 'right' },
    soldDateColumn
]

const meterAssetDetailColumns = [
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (asset) => deviceLabel(asset) },
    { title: 'Status', key: 'status', render: (value, record) => (record.asset && record.asset.status) || '-' },
    soldDateColumn
]

const assetCountDetailColumns = [
    { title: 'Asset', dataIndex: 'asset', key: 'asset', render: (asset) => deviceLabel(asset) },
    { title: 'Asset Model', key: 'assetModel', render: (value, record) => (record.assetModel && record.assetModel.name) || '-' },
    { title: 'Status', key: 'status', render: (value, record) => (record.asset && record.asset.status) || '-' },
    { title: 'Countable', key: 'countable', render: (value, record) => (record.countable ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>) },
    soldDateColumn
]

// Distinct countable asset models within a set of asset-count detail rows (for the model-count details).
const buildDistinctModels = (items) => {
    const byModel = {}
    ;(items || []).forEach((item) => {
        if (item.countable && item.assetModel && item.assetModel._id) {
            const id = item.assetModel._id
            if (!byModel[id]) {
                byModel[id] = { key: id, model: item.assetModel.name || id, assetCount: 0 }
            }
            byModel[id].assetCount += 1
        }
    })
    return Object.keys(byModel).map((id) => byModel[id])
}

const modelDetailColumns = [
    { title: 'Asset Model', dataIndex: 'model', key: 'model' },
    { title: 'Assets', dataIndex: 'assetCount', key: 'assetCount', align: 'right' }
]

const detailColumnsByReport = {
    gps: gpsDetailColumns,
    fuelSensor: fuelSensorDetailColumns,
    ble: bleDetailColumns,
    manualFuel: manualFuelDetailColumns,
    meterOn: meterAssetDetailColumns,
    meterOff: meterAssetDetailColumns,
    assetCount: assetCountDetailColumns
}

const countTitleByReport = {
    gps: 'Asset Count',
    fuelSensor: 'Asset Count',
    ble: 'Asset Count',
    manualFuel: 'Asset Count',
    meterOn: 'Asset Count',
    meterOff: 'Asset Count'
}

const buildParentColumns = (reportType) => {
    if (reportType === 'assetCount') {
        return [
            { title: 'Organisation', dataIndex: 'organisationName', key: 'organisationName' },
            { title: 'Unique Assets', dataIndex: 'count', key: 'count', align: 'right', render: (value) => <strong>{value}</strong> },
            { title: 'Countable Models', dataIndex: 'countableCount', key: 'countableCount', align: 'right', render: (value) => <strong>{value}</strong> }
        ]
    }
    return [
        { title: 'Organisation', dataIndex: 'organisationName', key: 'organisationName' },
        { title: countTitleByReport[reportType] || 'Count', dataIndex: 'count', key: 'count', align: 'right', render: (value) => <strong>{value}</strong> }
    ]
}

// Flattens all fetched reports into one consolidated list: Organisation | Report | Count.
// Asset count contributes two rows per org (unique assets, and countable model count).
const buildConsolidatedRows = (sections) => {
    const rows = []
    sections.forEach((section) => {
        (section.groups || []).forEach((group) => {
            if (section.reportType === 'assetCount') {
                rows.push({ key: `${section.key}-${group.key}-unique`, organisationName: group.organisationName, report: 'Asset count (unique)', count: group.count, reportType: 'assetCount', variant: 'unique', items: group.items })
                rows.push({ key: `${section.key}-${group.key}-model`, organisationName: group.organisationName, report: 'Asset count (model count)', count: group.countableCount, reportType: 'assetCount', variant: 'model', items: group.items })
            } else {
                rows.push({ key: `${section.key}-${group.key}`, organisationName: group.organisationName, report: section.label, count: group.count, reportType: section.reportType, variant: null, items: group.items })
            }
        })
    })
    // Stable sort by organisation groups each org's reports together (report order preserved).
    rows.sort((a, b) => (a.organisationName || '').localeCompare(b.organisationName || ''))
    return rows
}

const consolidatedColumns = [
    {
        title: 'Organisation',
        dataIndex: 'organisationName',
        key: 'organisationName',
        sorter: (a, b) => (a.organisationName || '').localeCompare(b.organisationName || '')
    },
    {
        title: 'Report',
        dataIndex: 'report',
        key: 'report',
        sorter: (a, b) => (a.report || '').localeCompare(b.report || '')
    },
    {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        align: 'right',
        sorter: (a, b) => (a.count || 0) - (b.count || 0),
        render: (value) => <strong>{value}</strong>
    }
]

// --- Excel export --------------------------------------------------------

const soldText = (item) => (item.soldDate ? formatDateTime(item.soldDate) : '-')

// One flattened (plain-object) detail row for Excel, keyed by report type.
const detailRowFor = (reportType, organisationName, item) => {
    if (reportType === 'fuelSensor') {
        return {
            Organisation: organisationName,
            'Fuel Sensor': deviceLabel(item.fuelSensor),
            'Fuel Sensor Id': (item.fuelSensor && item.fuelSensor.fuelSensorId) || '-',
            'GPS Device': deviceLabel(item.gpsDevice),
            Asset: deviceLabel(item.asset),
            'Attached Date': formatDateTime(item.attachedDate),
            'Deattached Date': item.stillAttached ? 'Still attached' : formatDateTime(item.deattachedDate),
            'Sold Date': soldText(item)
        }
    }
    if (reportType === 'ble') {
        return {
            Organisation: organisationName,
            'BLE Device': deviceLabel(item.bleDevice),
            'BLE Tag Id': (item.bleDevice && item.bleDevice.bleTagId) || '-',
            Asset: deviceLabel(item.asset),
            'Attached Date': formatDateTime(item.attachedDate),
            'Deattached Date': item.stillAttached ? 'Still attached' : formatDateTime(item.deattachedDate),
            'Sold Date': soldText(item)
        }
    }
    if (reportType === 'manualFuel') {
        return {
            Organisation: organisationName,
            Asset: deviceLabel(item.asset),
            Status: (item.asset && item.asset.status) || '-',
            'Fuel Entries': item.fuelEntryCount,
            'Sold Date': soldText(item)
        }
    }
    if (reportType === 'meterOn' || reportType === 'meterOff') {
        return {
            Organisation: organisationName,
            Asset: deviceLabel(item.asset),
            Status: (item.asset && item.asset.status) || '-',
            'Sold Date': soldText(item)
        }
    }
    if (reportType === 'assetCount') {
        return {
            Organisation: organisationName,
            Asset: deviceLabel(item.asset),
            'Asset Model': (item.assetModel && item.assetModel.name) || '-',
            Status: (item.asset && item.asset.status) || '-',
            Countable: item.countable ? 'Yes' : 'No',
            'Sold Date': soldText(item)
        }
    }
    // gps (default)
    return {
        Organisation: organisationName,
        'GPS Device': deviceLabel(item.gpsDevice),
        'GPRS/IMEI Id': (item.gpsDevice && item.gpsDevice.tagDeviceId) || '-',
        Asset: deviceLabel(item.asset),
        'Attached Date': formatDateTime(item.attachedDate),
        'Deattached Date': item.stillAttached ? 'Still attached' : formatDateTime(item.deattachedDate),
        'Sold Date': soldText(item)
    }
}

const flattenDetails = (section) => {
    const rows = []
    ;(section.groups || []).forEach((group) => {
        (group.items || []).forEach((item) => {
            rows.push(detailRowFor(section.reportType, group.organisationName, item))
        })
    })
    return rows
}

// Excel sheet names must be <= 31 chars and free of \ / ? * [ ] :
const sheetName = (label) => (label || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)

const summaryText = (section) => {
    if (section.reportType === 'assetCount') {
        return <span>Unique Assets: <strong>{section.totalCount}</strong> &middot; Countable Models: <strong>{section.totalCountable}</strong></span>
    }
    return <span>Total count: <strong>{section.totalCount}</strong></span>
}

// --- component -----------------------------------------------------------

const BillingUnits = ({ loading, authUser, form }) => {
    const { getFieldDecorator } = form
    const [fetching, setFetching] = useState(false)
    const [organisationLoading, setOrganisationLoading] = useState(false)
    const [organisations, setOrganisations] = useState([])
    const [sections, setSections] = useState([])
    const [mode, setMode] = useState('single')
    const [showDetails, setShowDetails] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)

    React.useEffect(() => {
        let mounted = true
        const fetchOrganisations = async () => {
            try {
                setOrganisationLoading(true)
                const response = await axios.get('/organisation/distinct')
                const orgs = Array.isArray(response && response.data) ? response.data : []
                if (mounted) {
                    setOrganisations(orgs)
                }
            } catch (error) {
                if (mounted) {
                    setOrganisations([])
                    message.error('Failed to load organisations')
                }
            } finally {
                if (mounted) {
                    setOrganisationLoading(false)
                }
            }
        }
        fetchOrganisations()
        return () => {
            mounted = false
        }
    }, [])

    const onSubmit = (e) => {
        e.preventDefault()
        form.validateFields(async (err, values) => {
            if (err) {
                message.error('Please select a valid period')
                return
            }

            // The API expects a period: `from` = start-of-day epoch-ms, `to` = end-of-day epoch-ms.
            const [fromDate, toDate] = values.period || []
            const params = {
                from: moment(fromDate).startOf('day').valueOf(),
                to: moment(toDate).endOf('day').valueOf()
            }
            if (values.organisationId) {
                params.organisation = values.organisationId
            }

            // "Request all" fetches every report; otherwise just the selected one.
            const selectedKeys = values.requestAll ? REPORTS.map((r) => r.key) : [values.reportType || 'gps']

            try {
                setFetching(true)
                const results = await Promise.all(
                    selectedKeys.map(async (key) => {
                        const report = REPORTS.find((r) => r.key === key)
                        try {
                            const response = await axios.get(report.endpoint, { params })
                            const data = (response && response.data) || {}
                            return {
                                key,
                                label: report.label,
                                reportType: key,
                                groups: buildGroups(data.data, key),
                                totalCount: typeof data.totalCount === 'number' ? data.totalCount : 0,
                                totalCountable: typeof data.totalCountableCount === 'number' ? data.totalCountableCount : 0,
                                billedPeriod: data.billedPeriod || null
                            }
                        } catch (error) {
                            console.error(`Error loading report ${key}:`, error)
                            message.error(`Failed to load: ${report.label}`)
                            return {
                                key,
                                label: report.label,
                                reportType: key,
                                groups: [],
                                totalCount: 0,
                                totalCountable: 0,
                                billedPeriod: null
                            }
                        }
                    })
                )
                setSections(results)
                setMode(values.requestAll ? 'all' : 'single')
                setShowDetails(!!values.detailsRequired)
                setHasSearched(true)
            } finally {
                setFetching(false)
            }
        })
    }

    const onExport = () => {
        if (!sections.length) {
            message.error('Nothing to export. Generate a report first.')
            return
        }
        const workbook = XLSX.utils.book_new()

        // Counts sheet (Organisation | Report | Count) -- same list as the consolidated view.
        const countRows = buildConsolidatedRows(sections).map((r) => ({
            Organisation: r.organisationName,
            Report: r.report,
            Count: r.count
        }))
        const countsWs = XLSX.utils.json_to_sheet(countRows.length ? countRows : [{ Organisation: '', Report: '', Count: '' }])
        XLSX.utils.book_append_sheet(workbook, countsWs, 'Counts')

        // Detail sheets only when "include details" was requested.
        if (showDetails) {
            sections.forEach((section) => {
                const rows = flattenDetails(section)
                if (rows.length) {
                    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), sheetName(section.label))
                }
            })
        }

        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' })
        saveAs(blob, `billing-units-${new Date().getTime()}.xlsx`)
        message.success('Report downloaded')
    }

    faviconDynamicFunction(authUser)

    const requestAllChecked = !!form.getFieldValue('requestAll')

    // Details for a consolidated count row: the model list for the asset-count "model" row, else the
    // report's own detail rows.
    const consolidatedExpandRender = (record) => {
        if (record.reportType === 'assetCount' && record.variant === 'model') {
            return (
                <Table
                    size="small"
                    rowKey="key"
                    columns={modelDetailColumns}
                    dataSource={buildDistinctModels(record.items)}
                    pagination={false}
                    scroll={{ x: true }}
                />
            )
        }
        return (
            <Table
                size="small"
                rowKey="key"
                columns={detailColumnsByReport[record.reportType] || gpsDetailColumns}
                dataSource={record.items || []}
                pagination={false}
                scroll={{ x: true }}
            />
        )
    }

    return (
        <Spin spinning={loading || fetching}>
            <Card className={`gx-card-metrics`}>
                <span>
                    <h2>Billing Units</h2>
                    <p>
                        Reports over a period (From start-of-day → To end-of-day). Tick "Request all" to fetch
                        every report at once, or pick a single report. Expand an organisation to see its rows.
                    </p>
                </span>

                <div style={{ marginTop: '20px', marginLeft: '2%', width: '60%' }}>
                    <Form layout="vertical" onSubmit={onSubmit}>
                        <Form.Item>
                            {getFieldDecorator('requestAll', { valuePropName: 'checked', initialValue: false })(
                                <Checkbox>Request all reports</Checkbox>
                            )}
                            {getFieldDecorator('detailsRequired', { valuePropName: 'checked', initialValue: false })(
                                <Checkbox style={{ marginLeft: '16px' }}>Include details in consolidated list</Checkbox>
                            )}
                        </Form.Item>

                        <Form.Item label="Report">
                            {getFieldDecorator('reportType', { initialValue: 'gps' })(
                                <Radio.Group buttonStyle="solid" disabled={requestAllChecked}>
                                    {REPORTS.map((r) => (
                                        <Radio.Button key={r.key} value={r.key}>{r.label}</Radio.Button>
                                    ))}
                                </Radio.Group>
                            )}
                        </Form.Item>

                        <Form.Item label="Organisation (Optional)">
                            {getFieldDecorator('organisationId')(
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="All Organisations"
                                    style={{ width: '100%' }}
                                    loading={organisationLoading}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {organisations.map((org) => (
                                        <Option key={String(org._id)} value={String(org._id)}>
                                            {org.name || org.code || String(org._id)}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>

                        <Form.Item label="Period (From start-of-day → To end-of-day)">
                            {getFieldDecorator('period', {
                                initialValue: [moment().startOf('month'), moment()],
                                rules: [{ required: true, message: 'Period is required!' }]
                            })(
                                <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} allowClear={false} />
                            )}
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                className="gx-mb-0"
                                htmlType="submit"
                                loading={fetching}
                                size="large"
                                disabled={fetching}
                            >
                                {fetching ? 'Loading...' : 'Generate'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>

                <div style={{ marginTop: '10px' }}>
                    {!hasSearched && <p>Select a period and click "Generate".</p>}

                    {hasSearched && sections.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <Button icon="download" onClick={onExport}>Export to Excel</Button>
                            {showDetails
                                ? <span style={{ marginLeft: '10px', color: '#8c8c8c' }}>counts + details</span>
                                : <span style={{ marginLeft: '10px', color: '#8c8c8c' }}>counts only</span>}
                        </div>
                    )}

                    {hasSearched && mode === 'all' && (
                        <div>
                            {sections[0] && sections[0].billedPeriod && (
                                <p style={{ marginBottom: '8px' }}>
                                    Consolidated report &middot; Period <strong>{sections[0].billedPeriod.label}</strong>
                                </p>
                            )}
                            <Table
                                size="small"
                                rowKey="key"
                                columns={consolidatedColumns}
                                dataSource={buildConsolidatedRows(sections)}
                                expandedRowRender={showDetails ? consolidatedExpandRender : undefined}
                                pagination={{ pageSize: 50, showSizeChanger: true }}
                                locale={{ emptyText: 'No records for this period' }}
                            />
                        </div>
                    )}

                    {hasSearched && mode === 'single' && sections.map((section) => (
                        <div key={section.key} style={{ marginBottom: '28px' }}>
                            <h3 style={{ marginBottom: '4px' }}>{section.label}</h3>
                            <p style={{ marginBottom: '8px' }}>
                                {section.billedPeriod && <span>Period <strong>{section.billedPeriod.label}</strong> &middot; </span>}
                                {summaryText(section)}
                            </p>
                            <Table
                                size="small"
                                rowKey="key"
                                columns={buildParentColumns(section.reportType)}
                                dataSource={section.groups}
                                expandedRowRender={(record) => (
                                    <Table
                                        size="small"
                                        rowKey="key"
                                        columns={detailColumnsByReport[section.reportType] || gpsDetailColumns}
                                        dataSource={record.items}
                                        pagination={false}
                                        scroll={{ x: true }}
                                    />
                                )}
                                pagination={{ pageSize: 25, showSizeChanger: true }}
                                locale={{ emptyText: 'No records for this report' }}
                            />
                        </div>
                    ))}
                </div>
            </Card>
        </Spin>
    )
}

const mapStateToProps = ({ commonData, auth }) => {
    return {
        loading: commonData.loading,
        authUser: auth.authUser || commonData.authUser
    }
}

export default connect(mapStateToProps, {})(Form.create()(BillingUnits))
